import { Pagination } from '@app/components'
import { usePaymentReports } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  createStyles,
  LoadingOverlay,
  Stack,
  type StackProps,
  Table,
  Text
} from '@mantine/core'
import { useDidUpdate } from '@mantine/hooks'
import { IconAlertCircle, IconDownload } from '@tabler/icons-react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { map } from 'lodash'
import { type FC, useState } from 'react'

dayjs.extend(localizedFormat)

const useStyles = createStyles(() => ({
  actionColumn: {
    textAlign: 'center',
    verticalAlign: 'center',
    width: 64
  }
}))

export interface PaymentReportsTableProps extends Omit<StackProps, 'children'> {}

export const PaymentReportsTable: FC<PaymentReportsTableProps> = (props) => {
  const { siteId } = useSugarContext()
  const [page, setPage] = useState(1)
  const {
    data: paymentReports,
    error,
    isError,
    isFetching: isFetchingPaymentReports,
    refetch
  } = usePaymentReports({ page })
  const { classes } = useStyles()

  useDidUpdate(() => {
    setPage(1)
  }, [siteId])

  return (
    <Stack
      pos='relative'
      spacing='lg'
      {...props}
    >
      <LoadingOverlay visible={isFetchingPaymentReports} />
      {isError ? (
        <Alert
          color='red'
          icon={<IconAlertCircle size={16} />}
          title='Could not load payment reports'
        >
          <Stack spacing='sm'>
            <Text size='sm'>
              {(error as Error | null)?.message ?? 'Something went wrong loading payment reports. Please try again.'}
            </Text>
            <Box>
              <Button
                color='red'
                onClick={() => refetch()}
                size='xs'
                variant='light'
              >
                Retry
              </Button>
            </Box>
          </Stack>
        </Alert>
      ) : (
        <>
          <Table
            horizontalSpacing='xs'
            verticalSpacing='xs'
            withBorder
          >
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th className={classes.actionColumn}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paymentReports?.data?.length ? (
                map(paymentReports.data, (paymentReport, idx) => (
                  <tr key={paymentReport.docId}>
                    <td>{paymentReport.docName}</td>
                    <td>{dayjs(paymentReport.docCreated).format('LLL')}</td>
                    <td className={classes.actionColumn}>
                      <ActionIcon
                        color='brand-red'
                        component='a'
                        href={`/api/payment-reports/${paymentReport.docId}`}
                        tabIndex={0}
                        title='Download Payment Report'
                        variant='transparent'
                      >
                        <IconDownload size={16} />
                      </ActionIcon>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3}>
                    <Text
                      align='center'
                      color='dimmed'
                      py='md'
                    >
                      No payment reports available yet.
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <Pagination
            onChange={setPage}
            total={paymentReports?.pagination?.totalPages || 1}
            value={page}
          />
        </>
      )}
    </Stack>
  )
}
